/**
 * Procedural poses (05 §6.2): no keyframes, a gait is a few phase-shifted sines.
 *
 * The body (pelvis, legs) always faces the way the mesh is turned - the direction of travel.
 * `twist` then turns the chest, head and rifle toward the aim, so a man can walk one way and
 * cover another without his legs ever pointing somewhere his feet are not going.
 *
 * This file chooses; the poses themselves live in poseBase.ts (standing, moving, lying),
 * poseActions.ts (recoil, stagger, throw, stab), poseStates.ts (deaths, wounded, prisoner, ladder) and
 * poseCrouch.ts (the crouched walk and the crouch of a sneaking man).
 */

import type { Bone } from 'three';
import { flinch, flinchAt, handsUp, kickAt, kickProne, kickStanding, stabPose, throwPose } from './poseActions';
import {
  aimStance, type BoneOf, carry, crawl, dead, locomotion, type Motion, type PoseInput, resetPose, type Rig, shouldered,
} from './poseBase';
import { crouch, crouchAim, sneak } from './poseCrouch';
import { boardPose, deathPose, surrenderPose, woundedPose } from './poseStates';
import { B } from './rig';

export type { Motion, PoseInput } from './poseBase';
export { HIT_SECONDS, STAB_SECONDS, THROW_RELEASE_AT, THROW_SECONDS } from './poseActions';
export { BLAST_SECONDS, CRUMPLE_SECONDS, WOUNDED_FALL_SECONDS } from './poseStates';

/** Every motion, in the order the pose viewer and the tests walk them. */
export const MOTIONS: readonly Motion[] = [
  'idle', 'walk', 'run', 'crawl', 'aim', 'dead', 'fire', 'fireProne', 'throw', 'stab', 'hit',
  'deathCrumple', 'deathBlast', 'wounded', 'surrender', 'board', 'sneak', 'crouch', 'crouchAim',
];

/** Seconds a one-shot lasts, for whoever loops it (the pose viewer) or must know when it ends; 0 = it loops itself. */
export const MOTION_SECONDS: Readonly<Record<Motion, number>> = {
  idle: 0, walk: 0, run: 0, crawl: 0, aim: 0, dead: 0, wounded: 0, surrender: 0, board: 0,
  sneak: 0, crouch: 0, crouchAim: 0,
  fire: 0.1, fireProne: 0.1, throw: 0.6, stab: 0.3, hit: 0.35, deathCrumple: 0.75, deathBlast: 0.8,
};

/** Motions that take the recoil and the stagger as overlays: the upright gaits, and the crouches. */
const UPRIGHT_OVERLAYS: ReadonlySet<Motion> = new Set<Motion>(['walk', 'run', 'idle', 'sneak', 'crouch', 'crouchAim']);

export function applyPose(bones: readonly Bone[], bone: BoneOf, input: PoseInput, t: number): void {
  const rig: Rig = { bones, bone };
  const age = Math.max(0, input.age ?? 0);
  resetPose(bones);

  switch (input.motion) {
    case 'idle':
      carry(bone);
      bone(B.spine).rotation.x = 0.04 + Math.sin(t * 1.8) * 0.02;
      break;
    case 'walk':
    case 'run':
      carry(bone);
      locomotion(bone, input);
      break;
    case 'aim':
      aimStance(bone);
      break;
    case 'sneak':
      sneak(bone, input);
      break;
    case 'crouch':
      crouch(bone, input, t);
      break;
    case 'crouchAim':
      crouchAim(bone);
      break;
    case 'fire':
      aimStance(bone);
      kickStanding(bone, kickAt(age));
      break;
    case 'hit':
      carry(bone);
      flinch(bone, flinchAt(age));
      break;
    case 'throw':
      throwPose(rig, age);
      break;
    case 'stab':
      stabPose(rig, age);
      break;
    case 'crawl':
      crawl(bone, input);
      return; // a prone man turns his whole body, not his spine
    case 'fireProne':
      crawl(bone, input);
      kickProne(bone, kickAt(age));
      return;
    case 'dead':
      dead(bone);
      return;
    case 'deathCrumple':
    case 'deathBlast':
      deathPose(rig, input.motion === 'deathBlast', age);
      return;
    case 'wounded':
      woundedPose(rig, input.age ?? Number.POSITIVE_INFINITY, t, input.vigour ?? 1);
      return;
    case 'surrender':
      surrenderPose(bone);
      return;
    case 'board':
      boardPose(bone, age);
      return;
  }

  // Upper body turns toward the aim: most of it in the spine, the rest in the neck. When
  // he is covering a flank on the move, or firing on the move, the rifle comes up to the shoulder.
  const kick = input.kick ?? 0;
  const moving = UPRIGHT_OVERLAYS.has(input.motion);
  // The crouches raise the rifle themselves, to a shoulder that is not where an upright man's is.
  const upright = input.motion === 'walk' || input.motion === 'run' || input.motion === 'idle';
  if (upright && (Math.abs(input.twist) > 0.6 || kick > 0)) shouldered(bone);
  bone(B.spine).rotation.y += input.twist * 0.7;
  bone(B.head).rotation.y += input.twist * 0.3;
  if (moving) kickStanding(bone, kick);
  flinch(bone, input.flinch ?? 0);
  if (input.handsUp) handsUp(bone);
}
